import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { useUserContext } from '@/Contexts/Authenticated/UserContext';
import { CustomWorklist } from '@/Models/GenericWorklist/CustomWorklist';
import GroupLevel from '@/Pages/GenericWorklist/Components/GroupLevel';
import { DobInput, DobSelect } from '@dedalusdiit/dob-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../Components/Button/Button';
import { IWorklistStore } from '../../Stores/WorklistStore';
import Style from './SaveWorklistDialog.module.scss';
import { SaveWorklistDialogViewStore } from './SaveWorklistDialogViewStore';

export interface SaveWorklistDialogProps {
    activeWorklist?: CustomWorklist;
    isOpen: boolean;
    worklistStore: IWorklistStore;
    onSave: (name: string, groupLevel: GroupLevel, groupID?: number, description?: string) => Promise<void>;
    onClose: () => void;
}

const {
    saveWorklistForm: saveWorklistFormStyle,
    invisible: invisibleStyle,
    saveWorklistDialog: saveWorklistDialogStyle,
} = Style;

const SaveWorklistDialog = (props: Readonly<SaveWorklistDialogProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { user } = useUserContext();

    const { activeWorklist, worklistStore, isOpen, onSave, onClose } = props;
    const viewStore = useMemo(() => new SaveWorklistDialogViewStore(activeWorklist, t, user, worklistStore), [worklistStore, activeWorklist, isOpen, user, t]); // Add isOpen dependency to make sure this resets whenever it is closed, to not reset the defaults
    const {
        description,
        groupID,
        groupOptionSelected,
        groupOptions,
        groupSelectionVisible,
        isValidToSave,
        name,
        resetSelections,
        scope,
        scopeOptionSelected,
        scopeOptions,
        setDescription,
        setGroupID,
        setName,
        setScope,
    } = viewStore;

    const onSaveClickHandler = (): void => {
        if (!viewStore.isValidToSave) {
            throw new Error(`Should not be possible to save a worklist while isValidToSave is not true`);
        }
        if (!name) {
            throw new Error('Should not be possible to save a worklist with an empty name');
        }
        void onSave(name, scope, groupID, description)
            .then(() => {
                resetSelections();
                onClose();
            });
    };

    const onCloseClickHandler = (): void => {
        resetSelections();
        onClose();
    };

    return (
        <Dialog open={isOpen} className={saveWorklistDialogStyle}>
            <DialogTitle>{t('SaveWorklist')}</DialogTitle>
            <DialogContent>
                <form className={saveWorklistFormStyle}>
                    <DobInput
                        key="Name"
                        autoFocus
                        direction="vertical"
                        label={t('Name')}
                        onValueChange={(newName) => setName(newName)}
                        value={name}
                    />
                    <DobInput
                        key="Description"
                        direction="vertical"
                        label={t('Description')}
                        onValueChange={(newName) => setDescription(newName)}
                        value={description}
                    />
                    <DobSelect
                        key="Scope"
                        direction="vertical"
                        label={t('GroupLevel')}
                        onChange={(option) => {
                            if (option) {
                                setScope(option.value);
                            }
                        }}
                        optionSelected={scopeOptionSelected}
                        options={scopeOptions}
                        menuPosition="absolute"
                    />
                    <DobSelect
                        key="Group"
                        className={classNames({ [invisibleStyle]: !groupSelectionVisible })}
                        direction="vertical"
                        label={t('Group')}
                        onChange={(option) => {
                            if (option) {
                                setGroupID(option.value);
                            }
                        }}
                        optionSelected={groupOptionSelected}
                        options={groupOptions}
                        menuPosition="absolute"
                    />
                </form>
            </DialogContent>
            <DialogButtons>
                <Button disabled={!isValidToSave} onClick={onSaveClickHandler} mediumGreen>{t('Save')}</Button>
                <Button onDark silent onClick={onCloseClickHandler}>{t('Cancel')}</Button>
            </DialogButtons>
        </Dialog>
    );
};

export default observer(SaveWorklistDialog);
